import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Mail,
  Users,
  Settings,
  Server,
  Trash2,
  Plus,
  Pencil,
  Shield,
  ShieldCheck,
  Loader2,
  Send,
  Save,
  HardDrive,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const SMTPSettingsTab = () => {
  const [settings, setSettings] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    encryption: "tls",
    from_email: "",
    from_name: "NoteKeeper"
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/admin/settings/smtp`);
        if (response.data && response.data.host) {
          setSettings(prev => ({ ...prev, ...response.data, password: "" }));
        }
      } catch (error) {
        console.error("Failed to fetch SMTP settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API_URL}/admin/settings/smtp`, settings);
      toast.success("SMTP settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    try {
      await axios.post(`${API_URL}/admin/settings/smtp/test`);
      toast.success("Test email sent successfully!");
    } catch (error) {
      toast.error("Failed to send test email");
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5" />
          SMTP Configuration
        </CardTitle>
        <CardDescription>
          Configure email server for password recovery and notifications
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>SMTP Host</Label>
            <Input
              value={settings.host}
              onChange={(e) => setSettings(prev => ({ ...prev, host: e.target.value }))}
              placeholder="smtp.example.com"
              data-testid="smtp-host-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Port</Label>
            <Input
              type="number"
              value={settings.port}
              onChange={(e) => setSettings(prev => ({ ...prev, port: parseInt(e.target.value) || 587 }))}
              data-testid="smtp-port-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Username</Label>
            <Input
              value={settings.username}
              onChange={(e) => setSettings(prev => ({ ...prev, username: e.target.value }))}
              placeholder="your@email.com"
              data-testid="smtp-username-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Password</Label>
            <Input
              type="password"
              value={settings.password}
              onChange={(e) => setSettings(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Enter password"
              data-testid="smtp-password-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Encryption</Label>
            <Select
              value={settings.encryption}
              onValueChange={(value) => setSettings(prev => ({ ...prev, encryption: value }))}
            >
              <SelectTrigger data-testid="smtp-encryption-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tls">TLS</SelectItem>
                <SelectItem value="ssl">SSL</SelectItem>
                <SelectItem value="none">None</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>From Email</Label>
            <Input
              value={settings.from_email}
              onChange={(e) => setSettings(prev => ({ ...prev, from_email: e.target.value }))}
              placeholder="noreply@example.com"
              data-testid="smtp-from-email-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>From Name</Label>
          <Input
            value={settings.from_name}
            onChange={(e) => setSettings(prev => ({ ...prev, from_name: e.target.value }))}
            placeholder="NoteKeeper"
            data-testid="smtp-from-name-input"
          />
        </div>

        <div className="flex gap-2 pt-4">
          <Button onClick={handleSave} disabled={isSaving} data-testid="save-smtp-button">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
          <Button variant="outline" onClick={handleTest} disabled={isTesting} data-testid="test-smtp-button">
            {isTesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Send Test Email
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user: currentUser } = useAuth();

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleToggleAdmin = async (userId, currentIsAdmin) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}`, null, {
        params: { is_admin: !currentIsAdmin }
      });
      fetchUsers();
      toast.success("User updated");
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`);
      fetchUsers();
      toast.success("User deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          User Management
        </CardTitle>
        <CardDescription>Manage user accounts and permissions</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Username</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.is_admin ? (
                      <span className="flex items-center gap-1 text-primary">
                        <ShieldCheck className="w-4 h-4" />
                        Admin
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Shield className="w-4 h-4" />
                        User
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleAdmin(user.id, user.is_admin)}
                        disabled={user.id === currentUser?.id}
                      >
                        {user.is_admin ? "Remove Admin" : "Make Admin"}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={user.id === currentUser?.id}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

const AppSettingsTab = () => {
  const [settings, setSettings] = useState({
    trash_auto_delete_days: 30,
    todo_history_auto_delete_days: 30,
    storage_path: "/app/storage"
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/admin/settings/app`);
        if (response.data) {
          setSettings(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.error("Failed to fetch app settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API_URL}/admin/settings/app`, settings);
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const [isClearingHistory, setIsClearingHistory] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleClearHistory = async () => {
    setIsClearingHistory(true);
    try {
      await axios.delete(`${API_URL}/admin/clear-history`);
      setShowClearConfirm(false);
      toast.success("All history cleared");
    } catch (error) {
      toast.error("Failed to clear history");
    } finally {
      setIsClearingHistory(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Application Settings
          </CardTitle>
          <CardDescription>Configure application behavior</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <HardDrive className="w-4 h-4" />
              Notes Storage Path
            </Label>
            <Input
              value={settings.storage_path}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                storage_path: e.target.value
              }))}
              placeholder="/app/storage"
              data-testid="storage-path-input"
            />
            <p className="text-sm text-muted-foreground">
              Server filesystem path where notes will be stored. Changes apply to new operations.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Auto-delete trash after (days)</Label>
            <Input
              type="number"
              value={settings.trash_auto_delete_days}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                trash_auto_delete_days: parseInt(e.target.value) || 30
              }))}
              data-testid="trash-days-input"
            />
            <p className="text-sm text-muted-foreground">
              Items in trash will be permanently deleted after this many days
            </p>
          </div>

          <div className="space-y-2">
            <Label>Auto-delete completed tasks after (days)</Label>
            <Input
              type="number"
              value={settings.todo_history_auto_delete_days}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                todo_history_auto_delete_days: parseInt(e.target.value) || 30
              }))}
              data-testid="history-days-input"
            />
            <p className="text-sm text-muted-foreground">
              Completed tasks will be permanently deleted after this many days
            </p>
          </div>

          <Button onClick={handleSave} disabled={isSaving} data-testid="save-app-settings-button">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>

          {/* Clear History Section */}
          <div className="border-t pt-6 mt-6">
            <Label className="text-destructive flex items-center gap-2 mb-2">
              <Trash2 className="w-4 h-4" />
              Danger Zone
            </Label>
            <p className="text-sm text-muted-foreground mb-3">
              Clear all task history (completed and deleted items from both private and common todos).
            </p>
            <Button
              variant="destructive"
              onClick={() => setShowClearConfirm(true)}
              disabled={isClearingHistory}
              data-testid="clear-history-button"
            >
              {isClearingHistory ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Clear All History
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Clear History Confirmation */}
      <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All History</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all completed and deleted task history for all users.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearHistory} className="bg-destructive hover:bg-destructive/90">
              Clear All History
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const ConnectionsTab = () => {
  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newConnection, setNewConnection] = useState({
    name: "",
    protocol: "sftp",
    host: "",
    port: 22,
    username: "",
    password: "",
    base_path: "/"
  });

  const fetchConnections = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/connections`);
      setConnections(response.data);
    } catch (error) {
      toast.error("Failed to load connections");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const handleAddConnection = async () => {
    try {
      await axios.post(`${API_URL}/admin/connections`, newConnection);
      setShowModal(false);
      setNewConnection({
        name: "",
        protocol: "sftp",
        host: "",
        port: 22,
        username: "",
        password: "",
        base_path: "/"
      });
      fetchConnections();
      toast.success("Connection added");
    } catch (error) {
      toast.error("Failed to add connection");
    }
  };

  const handleDeleteConnection = async (connId) => {
    try {
      await axios.delete(`${API_URL}/admin/connections/${connId}`);
      fetchConnections();
      toast.success("Connection deleted");
    } catch (error) {
      toast.error("Failed to delete connection");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5" />
              External Connections
            </CardTitle>
            <CardDescription>Manage FTP, SFTP, and SMB connections</CardDescription>
          </div>
          <Button onClick={() => setShowModal(true)} data-testid="add-connection-button">
            <Plus className="w-4 h-4 mr-2" />
            Add Connection
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : connections.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No connections configured</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Protocol</TableHead>
                <TableHead>Host</TableHead>
                <TableHead>User</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {connections.map((conn) => (
                <TableRow key={conn.id}>
                  <TableCell className="font-medium">{conn.name}</TableCell>
                  <TableCell className="uppercase text-xs">{conn.protocol}</TableCell>
                  <TableCell>{conn.host}:{conn.port}</TableCell>
                  <TableCell>{conn.username}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => handleDeleteConnection(conn.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Add Connection Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add External Connection</DialogTitle>
            <DialogDescription>Configure a new FTP, SFTP, or SMB connection</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Connection Name</Label>
              <Input
                value={newConnection.name}
                onChange={(e) => setNewConnection(prev => ({ ...prev, name: e.target.value }))}
                placeholder="My Server"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Protocol</Label>
                <Select
                  value={newConnection.protocol}
                  onValueChange={(value) => setNewConnection(prev => ({
                    ...prev,
                    protocol: value,
                    port: value === "ftp" ? 21 : value === "smb" ? 445 : 22
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sftp">SFTP (SSH)</SelectItem>
                    <SelectItem value="ftp">FTP</SelectItem>
                    <SelectItem value="smb">SMB (Windows Share)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Port</Label>
                <Input
                  type="number"
                  value={newConnection.port}
                  onChange={(e) => setNewConnection(prev => ({ ...prev, port: parseInt(e.target.value) }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Host</Label>
              <Input
                value={newConnection.host}
                onChange={(e) => setNewConnection(prev => ({ ...prev, host: e.target.value }))}
                placeholder="192.168.1.100"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Username</Label>
                <Input
                  value={newConnection.username}
                  onChange={(e) => setNewConnection(prev => ({ ...prev, username: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={newConnection.password}
                  onChange={(e) => setNewConnection(prev => ({ ...prev, password: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Base Path</Label>
              <Input
                value={newConnection.base_path}
                onChange={(e) => setNewConnection(prev => ({ ...prev, base_path: e.target.value }))}
                placeholder="/"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleAddConnection}>Add Connection</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default function AdminPage() {
  const { user } = useAuth();

  if (!user?.is_admin) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="admin-page">
      <div className="border-b border-border px-6 py-4">
        <h1 className="text-xl font-semibold">Admin Panel</h1>
      </div>

      <Tabs defaultValue="smtp" className="flex-1 flex flex-col">
        <div className="border-b border-border px-6">
          <TabsList className="bg-transparent h-auto p-0 gap-4">
            <TabsTrigger
              value="smtp"
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-3"
            >
              <Mail className="w-4 h-4 mr-2" />
              SMTP
            </TabsTrigger>
            <TabsTrigger
              value="users"
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-3"
            >
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger
              value="connections"
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-3"
            >
              <Server className="w-4 h-4 mr-2" />
              Connections
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-3"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-6 max-w-4xl">
            <TabsContent value="smtp" className="mt-0">
              <SMTPSettingsTab />
            </TabsContent>
            <TabsContent value="users" className="mt-0">
              <UsersTab />
            </TabsContent>
            <TabsContent value="connections" className="mt-0">
              <ConnectionsTab />
            </TabsContent>
            <TabsContent value="settings" className="mt-0">
              <AppSettingsTab />
            </TabsContent>
          </div>
        </ScrollArea>
      </Tabs>
    </div>
  );
}
